"use client";

/**
 * MIN-293 — `AgentChat.Thread`: the transcript, composed as shadcn's Chat
 * pattern composes it. Each turn is a `MessageScrollerItem`; the person's turn
 * is the scroll anchor, so a new question settles near the top and the reply
 * streams in under it. `autoScroll` follows the reply while the reader stays at
 * the live edge, and `defaultScrollPosition="last-anchor"` reopens a thread on
 * its last question.
 *
 * Ours, with no shadcn counterpart: the content box carries the shared chat
 * measure (`data-chat-measure="transcript"`), so the transcript and the
 * composer sit on one 48rem column.
 *
 * `Thread`'s own state is `ChatBubble[]` (`useChat`'s `UIMessage[]` flattened
 * by `uiToBubbles`); `turnFromBubble` re-derives a `Turn` from a bubble's own
 * fields so an assistant turn renders through `AgentChat.Turn` and the merged
 * `PartRenderers` registry without re-parsing the raw message.
 */
import { Bubble, BubbleContent } from "@repo/ui/components/ui/bubble";
import { Message, MessageContent } from "@repo/ui/components/ui/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "@repo/ui/components/ui/message-scroller";
import { MESSAGE_ANIMATIONS } from "@repo/ui/lib/message-animations";
import { cn } from "@repo/ui/lib/utils";
import { motion, useReducedMotion } from "motion/react";
import { chatMeasureStyle } from "../elements/chat-measure";
import type { ChatBubble } from "../elements/chat-turn";
import type { Turn as TurnModel, TurnOutcome, TurnPart } from "../turn";
import { useAgentChatContext } from "./context";
import { AgentChatTurn, WorkingMarker } from "./turn";

export interface AgentChatThreadProps {
  className?: string;
}

const MotionMessageScrollerItem = motion.create(MessageScrollerItem);

/** `ChatBubble` → `Turn`. `isStreaming` comes from the surrounding thread, not
 * the bubble: a turn's streaming-ness is a property of the conversation. */
function turnFromBubble(bubble: ChatBubble, isStreaming: boolean): TurnModel {
  const parts: TurnPart[] = [];

  if (bubble.reasoning) parts.push({ kind: "reasoning", text: bubble.reasoning });

  for (const t of bubble.tools) {
    if (t.state === "error") {
      parts.push({
        kind: "tool",
        callId: t.toolCallId,
        name: t.toolName,
        state: "error",
        input: t.input,
        error: t.errorText ?? "",
      });
    } else if (t.state === "done") {
      parts.push({
        kind: "tool",
        callId: t.toolCallId,
        name: t.toolName,
        state: "done",
        input: t.input,
        output: t.output,
      });
    } else {
      parts.push({
        kind: "tool",
        callId: t.toolCallId,
        name: t.toolName,
        state: "running",
        ...(t.input !== undefined ? { input: t.input } : {}),
      });
    }
  }

  if (bubble.text) parts.push({ kind: "text", text: bubble.text });

  for (const o of bubble.opaque ?? []) {
    parts.push({ kind: "opaque", renderAs: o.renderAs, data: o.data });
  }

  for (const s of bubble.sources) {
    parts.push({ kind: "citation", id: s.id, title: s.title, detail: s.detail, excerpt: s.text, score: s.score });
  }

  const outcome: TurnOutcome = isStreaming ? "streaming" : bubble.interrupted ? "interrupted" : "complete";

  return { id: bubble.id, role: bubble.role, parts, outcome };
}

export function AgentChatThread({ className }: AgentChatThreadProps) {
  const { state } = useAgentChatContext();
  const shouldReduceMotion = useReducedMotion();
  const messages = state.messages;
  const last = messages.at(-1);
  // Only the pre-assistant gap needs its own status; once an assistant turn
  // exists, `AgentChat.Turn` owns the in-progress affordance.
  const showThinking = state.isStreaming && last?.role !== "assistant";

  return (
    <MessageScrollerProvider autoScroll defaultScrollPosition="last-anchor">
      <MessageScroller className={cn("min-h-0", className)}>
        <MessageScrollerViewport>
          <MessageScrollerContent
            aria-busy={state.isStreaming}
            className="gap-6 py-4"
            data-chat-measure="transcript"
            style={chatMeasureStyle}
          >
            {messages.map((message) => {
              if (message.role === "user") {
                return (
                  <MotionMessageScrollerItem
                    key={message.id}
                    messageId={message.id}
                    scrollAnchor
                    variants={MESSAGE_ANIMATIONS["slide-up"].variants}
                    initial={shouldReduceMotion ? false : "initial"}
                    animate="animate"
                  >
                    <Message align="end">
                      <MessageContent>
                        <Bubble align="end" variant="muted">
                          <BubbleContent>{message.text}</BubbleContent>
                        </Bubble>
                      </MessageContent>
                    </Message>
                  </MotionMessageScrollerItem>
                );
              }

              // Only the last assistant turn can still be streaming.
              const streaming = state.isStreaming && message === last;
              return (
                <MessageScrollerItem key={message.id} messageId={message.id}>
                  <AgentChatTurn turn={turnFromBubble(message, streaming)} />
                </MessageScrollerItem>
              );
            })}
            {showThinking && <WorkingMarker label="Thinking…" />}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}
